import { dateIsBefore } from 'src/utils/dates';
import { Membership, MembershipProps, PlanMemberAccessData } from './membership';
import { Metadata, Order, OrderData, WarrantyEvent } from './order';
import { OrganizationData, ProfessionalAccessData } from './professionalAccess';

export interface Headset {
  id: string;
  trialActivated: boolean;
  trialNumberDays: number;
  trialStartDate: {
    formatted: string;
  };
  mac?: string;
  firmwareVersion?: string;
  hardwareVersion?: string;
  modelNumber?: string;
  originalFirmwareVersion?: string;
  totalSessions?: number;
  totalAssessments?: number;
  firstSessionDate?: {
    formatted: string;
  };
  lastSessionDate?: {
    formatted: string;
  };
  userUsage?: string;
  warrantyEvents?: WarrantyEvent[];
}

export type HeadsetUserUsage = {
  userId?: string;
  email?: string;
  totalSessions?: number;
  totalAssessments?: number;
  firstSessionDate?: string;
  lastSessionDate?: string;
};

interface Role {
  id: string;
  name: string;
}
interface Protocol {
  id: string;
  name: string;
}

interface Feature {
  id: string;
  name: string;
}
interface Mission {
  id: string;
  name: string;
}
export type UserAddress = {
  firstName: string;
  lastName: string;
  cardHolderName: string;
  city: string;
  country: string;
  id: string;
  postal: string;
  province: string;
  street: string;
  unit: string;
  phone: string;
  vat: string;
};

export interface UserType {
  id: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  email: string;
  shippingAddresses?: UserAddress[];
  dateOfBirth?: {
    formatted: string;
  };
  unlockAccountAt?: {
    formatted: string;
  };
  membership?: MembershipProps;
  proMembership?: MembershipProps;
  expiredProMemberships?: MembershipProps[];
  expiredMemberships?: MembershipProps[];
  membershipPlanMemberAccess?: PlanMemberAccessData[];
  expiredMembershipPlanMemberAccess?: PlanMemberAccessData[];
  headset: Headset[];
  usedHeadsets: Headset[];
  orders?: OrderData[];

  proConfig?: {
    organizationName?: string;
  };
  // The graph exposes `organization` as a list (the upsert operates on
  // user.organization[0]); accept either shape from the query response.
  organization?: OrganizationData | OrganizationData[];
  affiliate?: {
    id: string;
  };
  expPoints?: number;
  currentLevel?: number;
  lastUsedMacAddress?: string;
  metadata: Metadata[];
  role?: Role[];
  protocols?: Protocol[];
  missions?: Mission[];
  features?: Feature[];
}

export class User {
  id: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  email: string;
  dateOfBirth?: Date;
  unlockAccountAt?: Date;
  shippingAddresses?: UserAddress[];
  membership?: Membership;
  proMembership?: Membership;
  expiredMemberships: Membership[];
  expiredProMemberships: Membership[];
  membershipPlanMemberAccess: PlanMemberAccessData[];
  expiredMembershipPlanMemberAccess: PlanMemberAccessData[];
  headset: Headset[];
  usedHeadsets: Headset[];
  orders: Order[];
  organizationName?: string;
  organization?: OrganizationData;
  affiliate?: {
    id: string;
  };
  role: Role[];

  expPoints?: number;
  currentLevel?: number;
  lastUsedMacAddress?: string;
  metadata: Metadata[];
  protocols: Protocol[];
  missions: Mission[];
  features: Feature[];

  constructor(props: UserType) {
    this.id = props.id;
    this.firstName = props.firstName;
    this.lastName = props.lastName;
    this.nickname = props.nickname;
    this.email = props.email;
    this.shippingAddresses = props.shippingAddresses;
    this.metadata = props.metadata;
    this.membership = props.membership
      ? new Membership(props.membership)
      : undefined;
    this.proMembership = props.proMembership
      ? new Membership(props.proMembership)
      : undefined;
    this.expiredMemberships = (props.expiredMemberships ?? [])
      .map((p) => new Membership(p))
      .sort(
        (a: Membership, b: Membership) =>
          a.endDate.getTime() - b.endDate.getTime()
      );
    this.headset = props.headset;
    this.usedHeadsets = props.usedHeadsets;
    this.expiredProMemberships = (props.expiredProMemberships ?? [])
      .map((p) => new Membership(p))
      .sort(
        (a: Membership, b: Membership) =>
          a.endDate.getTime() - b.endDate.getTime()
      );
    this.organizationName = props.proConfig?.organizationName;
    // Normalize the (list-shaped) organization relationship to a single node —
    // the admin flow operates on the user's first/only organization.
    this.organization = Array.isArray(props.organization)
      ? props.organization[0]
      : props.organization;
    this.orders = (props.orders ?? [])
      .sort(
        (a, b) =>
          new Date(b.created.formatted).getTime() -
          new Date(a.created.formatted).getTime()
      )
      .map((o) => new Order(o, this));
    this.affiliate = props.affiliate;
    this.dateOfBirth = props.dateOfBirth
      ? new Date(props.dateOfBirth.formatted)
      : undefined;

    this.currentLevel = props.currentLevel;
    this.expPoints = props.expPoints;
    this.lastUsedMacAddress = props.lastUsedMacAddress;

    this.unlockAccountAt = props.unlockAccountAt
      ? new Date(props.unlockAccountAt.formatted)
      : undefined;

    this.role = props.role ?? [];
    this.protocols = props.protocols ?? [];
    this.missions = props.missions ?? [];
    this.features = props.features ?? [];
    this.membershipPlanMemberAccess = props.membershipPlanMemberAccess ?? [];
    this.expiredMembershipPlanMemberAccess = props.expiredMembershipPlanMemberAccess ?? [];
  }

  hasPersonalMembership(): boolean {
    return !!this.membership && this.membership.status !== 'inactive';
  }

  hasProfessionalMembership(): boolean {
    return !!this.proMembership && this.proMembership.status !== 'inactive';
  }

  hasExpiredProMembership(): boolean {
    return this.expiredProMemberships.length > 0;
  }

  hasExpiredPersonalMembership(): boolean {
    return this.expiredMemberships.length > 0;
  }

  hasProConfig(): boolean {
    return !!this.organizationName;
  }

  activeProfessionalAccess(): ProfessionalAccessData[] {
    return this.organization?.activeProfessionalAccess ?? [];
  }

  expiredProfessionalAccess(): ProfessionalAccessData[] {
    return this.organization?.expiredProfessionalAccess ?? [];
  }

  hasActiveProfessionalAccess(): boolean {
    return this.activeProfessionalAccess().length > 0;
  }

  showProfessionalMembership(): boolean {
    return (
      this.hasProfessionalMembership() ||
      this.expiredProMemberships.length > 0 ||
      this.hasProConfig()
    );
  }
  isAccountLocked(): boolean {
    if (!this.unlockAccountAt) {
      return false;
    }
    return !dateIsBefore(this.unlockAccountAt, new Date());
  }
}
